import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Lock, Download, LogOut, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";

const TOKEN_KEY = "ur_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [authing, setAuthing] = useState(false);

  const fetchEntries = useCallback(async (tok) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/waitlist`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      setEntries(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        toast.error("Session expired. Please sign in again.");
      } else {
        toast.error("Could not load sign-ups.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) fetchEntries(token);
  }, [token, fetchEntries]);

  const login = async (e) => {
    e.preventDefault();
    if (!password) return;
    setAuthing(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setPassword("");
      toast.success("Welcome back.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Incorrect passcode.");
    } finally {
      setAuthing(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setEntries([]);
  };

  const exportCsv = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/waitlist.csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "upper-room-waitlist.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed.");
    }
  };

  /* ---------- Login gate ---------- */
  if (!token) {
    return (
      <div className="grain flex min-h-screen items-center justify-center bg-background px-6">
        <motion.form
          data-testid="admin-login-form"
          onSubmit={login}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-sm"
        >
          <div className="mb-8 flex h-12 w-12 items-center justify-center rounded-full bg-espresso text-cream">
            <Lock className="h-5 w-5" strokeWidth={1.5} />
          </div>
          <p className="font-body text-xs font-semibold uppercase tracking-[0.28em] text-terracotta">
            The Upper Room
          </p>
          <h1 className="mt-3 font-display text-4xl font-light tracking-tight text-espresso">
            Admin access
          </h1>
          <p className="mt-3 font-body text-sm font-light text-stone">
            Enter the passcode to view waitlist sign-ups.
          </p>
          <input
            data-testid="admin-password-input"
            type="password"
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Passcode"
            className="mt-8 w-full border-b border-espresso/20 bg-transparent py-3 font-body text-base text-espresso outline-none transition-colors duration-300 placeholder:text-stone/60 focus:border-espresso"
          />
          <button
            data-testid="admin-login-btn"
            type="submit"
            disabled={authing}
            className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-espresso px-8 py-4 font-body text-sm font-semibold uppercase tracking-[0.18em] text-cream transition-colors duration-300 hover:bg-[hsl(20_12%_25%)] disabled:opacity-60"
          >
            {authing ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enter"}
          </button>
        </motion.form>
      </div>
    );
  }

  /* ---------- Dashboard ---------- */
  return (
    <div className="grain min-h-screen bg-background px-6 py-12 sm:px-10">
      <div className="mx-auto max-w-[1200px]">
        <header className="flex flex-col gap-6 border-b border-espresso/10 pb-8 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="font-body text-xs font-semibold uppercase tracking-[0.28em] text-terracotta">
              Waitlist
            </p>
            <h1 className="mt-3 font-display text-4xl font-light tracking-tight text-espresso sm:text-5xl">
              Sign-ups
            </h1>
            <p data-testid="admin-count" className="mt-2 font-body text-sm font-light text-stone">
              {entries.length} {entries.length === 1 ? "person has" : "people have"} registered their interest.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              data-testid="admin-refresh-btn"
              onClick={() => fetchEntries(token)}
              className="inline-flex items-center gap-2 rounded-full border border-espresso/20 px-5 py-2.5 font-body text-xs font-semibold uppercase tracking-[0.14em] text-espresso transition-colors duration-300 hover:bg-sand"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </button>
            <button
              data-testid="admin-export-btn"
              onClick={exportCsv}
              disabled={entries.length === 0}
              className="inline-flex items-center gap-2 rounded-full bg-espresso px-5 py-2.5 font-body text-xs font-semibold uppercase tracking-[0.14em] text-cream transition-colors duration-300 hover:bg-[hsl(20_12%_25%)] disabled:opacity-50"
            >
              <Download className="h-3.5 w-3.5" /> Export CSV
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="inline-flex items-center gap-2 rounded-full px-3 py-2.5 font-body text-xs font-semibold uppercase tracking-[0.14em] text-stone transition-colors duration-300 hover:text-espresso"
            >
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        </header>

        {loading ? (
          <div className="flex items-center gap-3 py-24 font-body text-sm text-stone">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading sign-ups…
          </div>
        ) : entries.length === 0 ? (
          <div data-testid="admin-empty" className="py-24 font-display text-2xl font-light text-stone">
            No sign-ups yet.
          </div>
        ) : (
          <div className="mt-2 overflow-x-auto">
            <table data-testid="admin-table" className="w-full min-w-[760px] border-collapse">
              <thead>
                <tr className="border-b border-espresso/10 text-left">
                  {["#", "Name", "Email", "Phone", "Interest", "Message", "Date"].map((h) => (
                    <th key={h} className="py-4 pr-6 font-body text-[10px] font-semibold uppercase tracking-[0.2em] text-stone">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {entries.map((row, i) => (
                  <tr key={row.id} data-testid="admin-row" className="border-b border-espresso/10 align-top">
                    <td className="py-5 pr-6 font-display text-lg text-oak">{String(i + 1).padStart(2, "0")}</td>
                    <td className="py-5 pr-6 font-body text-sm text-espresso">{row.name}</td>
                    <td className="py-5 pr-6 font-body text-sm text-espresso">
                      <a className="link-underline" href={`mailto:${row.email}`}>{row.email}</a>
                    </td>
                    <td className="py-5 pr-6 font-body text-sm text-stone">{row.phone}</td>
                    <td className="py-5 pr-6 font-body text-sm text-stone">{row.interest}</td>
                    <td className="max-w-[220px] py-5 pr-6 font-body text-sm font-light text-stone">{row.message || "—"}</td>
                    <td className="py-5 pr-6 font-body text-xs text-stone/70">
                      {new Date(row.created_at).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
